import { create } from 'zustand';
import { validatePayload, type ValidationResult } from '@boletra/qr';
import { KEYS, storage } from './storage.js';

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_URL ?? 'http://localhost:3000';
const ONLINE_TIMEOUT_MS = 2500;

export type ScanResultKind = 'valid' | 'duplicate' | 'invalid';
export type ScanSource = 'online' | 'offline' | 'offline-cached';
export type InvalidReason = 'wrong_event' | 'tampered' | 'voided' | 'malformed' | 'unknown';

export interface ScanResult {
  result: ScanResultKind;
  source: ScanSource;
  ticket?: { name: string; kind: string };
  scanCount?: number;
  firstValidatedAt?: string | null;
  firstValidatedByDeviceId?: string | null;
  reason?: InvalidReason;
  qrId?: string;
}

interface SyncBundleQr {
  id: string;
  secret: string;
  status: 'issued' | 'validated';
  ticketSnapshot: { name: string; kind: string };
  validityFrom: string | null;
  validityTo: string | null;
  scanCount: number;
  lastScannedAt: string | null;
  validatedAt: string | null;
  validatedByDeviceId: string | null;
}

export interface SyncBundle {
  eventId: string;
  eventTitle: string;
  eventStartsAt: string | null;
  eventEndsAt: string | null;
  generatedAt: string;
  totalIssued: number;
  totalValidated: number;
  qrs: SyncBundleQr[];
}

interface PendingValidation {
  qrId: string;
  validatedAt: string;
  deviceId: string;
}

interface ScanHistoryEntry {
  scanCount: number;
  validatedAt: string | null;
  validatedByDeviceId: string | null;
}

export interface SessionStats {
  valid: number;
  duplicates: number;
  invalid: number;
  startedAt: string;
}

const EMPTY_STATS = (): SessionStats => ({
  valid: 0,
  duplicates: 0,
  invalid: 0,
  startedAt: new Date().toISOString(),
});

export interface ScannerSyncState {
  eventId: string | null;
  scannerToken: string | null;
  bundle: SyncBundle | null;
  pending: PendingValidation[];
  validatedLocally: Set<string>;
  scanHistory: Record<string, ScanHistoryEntry>;
  session: SessionStats;
  isSyncing: boolean;
  lastSyncAt: string | null;
  isOnline: boolean;

  load: (eventId: string, scannerToken: string | null) => void;
  pullBundle: (eventId: string, scannerToken: string) => Promise<void>;
  validate: (payload: string, deviceId: string) => Promise<ScanResult>;
  flushPending: (eventId: string, scannerToken: string) => Promise<{ flushed: number }>;
  resetSession: (eventId: string) => void;
}

export const useScannerSync = create<ScannerSyncState>((set, get) => ({
  eventId: null,
  scannerToken: null,
  bundle: null,
  pending: [],
  validatedLocally: new Set(),
  scanHistory: {},
  session: EMPTY_STATS(),
  isSyncing: false,
  lastSyncAt: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

  load(eventId, scannerToken) {
    const bundle = storage.get<SyncBundle>(KEYS.syncBundle(eventId));
    const pending = storage.get<PendingValidation[]>(KEYS.pendingValidations(eventId)) ?? [];
    const validated = storage.get<string[]>(KEYS.validatedQrs(eventId)) ?? [];
    const history = storage.get<Record<string, ScanHistoryEntry>>(KEYS.scanHistory(eventId)) ?? {};
    const session = storage.get<SessionStats>(KEYS.sessionStats(eventId)) ?? EMPTY_STATS();
    set({
      eventId,
      scannerToken,
      bundle,
      pending,
      validatedLocally: new Set(validated),
      scanHistory: history,
      session,
      lastSyncAt: bundle?.generatedAt ?? null,
    });
  },

  async pullBundle(eventId, scannerToken) {
    set({ isSyncing: true });
    try {
      const res = await fetch(`${API_BASE_URL}/scanner/sync/${eventId}`, {
        headers: { 'X-Scanner-Token': scannerToken },
      });
      if (!res.ok) throw new Error(`Sync failed: ${res.status}`);
      const bundle = (await res.json()) as SyncBundle;
      storage.set(KEYS.syncBundle(eventId), bundle);
      const prevHistory = get().scanHistory;
      const mergedHistory: Record<string, ScanHistoryEntry> = { ...prevHistory };
      for (const q of bundle.qrs) {
        const local = prevHistory[q.id];
        const localCount = local?.scanCount ?? 0;
        mergedHistory[q.id] = {
          scanCount: Math.max(localCount, q.scanCount),
          validatedAt: q.validatedAt ?? local?.validatedAt ?? null,
          validatedByDeviceId: q.validatedByDeviceId ?? local?.validatedByDeviceId ?? null,
        };
      }
      storage.set(KEYS.scanHistory(eventId), mergedHistory);
      set({
        bundle,
        scanHistory: mergedHistory,
        lastSyncAt: bundle.generatedAt,
        isOnline: true,
      });
    } catch (err) {
      set({ isOnline: false });
      throw err;
    } finally {
      set({ isSyncing: false });
    }
  },

  async validate(payload, deviceId) {
    const { bundle, scanHistory, session, eventId, scannerToken } = get();
    if (!eventId) {
      return { result: 'invalid', source: 'offline', reason: 'unknown' };
    }

    const validation: ValidationResult = await validatePayload(payload, (qrIdHex) => {
      const qr = bundle?.qrs.find((q) => q.id === qrIdHex);
      return qr ? base64ToBytes(qr.secret) : null;
    });

    if (!validation.ok) {
      const reason: InvalidReason = validation.reason;
      const next = { ...session, invalid: session.invalid + 1 };
      storage.set(KEYS.sessionStats(eventId), next);
      set({ session: next });
      return { result: 'invalid', source: 'offline', reason };
    }

    const qrId = validation.qrIdHex;
    const localQr = bundle?.qrs.find((q) => q.id === qrId);

    if (scannerToken) {
      try {
        const online = await callOnlineValidate(payload, deviceId, scannerToken);
        set({ isOnline: true });
        return applyOnlineResult(get, set, eventId, qrId, deviceId, localQr, online);
      } catch {
        set({ isOnline: false });
      }
    }

    if (!localQr) {
      const next = { ...session, invalid: session.invalid + 1 };
      storage.set(KEYS.sessionStats(eventId), next);
      set({ session: next });
      return { result: 'invalid', source: 'offline', reason: 'wrong_event' };
    }

    if (localQr.status === 'validated') {
      const history = scanHistory[qrId];
      const nextCount = (history?.scanCount ?? localQr.scanCount) + 1;
      const dup: ScanResult = {
        result: 'duplicate',
        source: 'offline-cached',
        qrId,
        ticket: localQr.ticketSnapshot,
        scanCount: nextCount,
        firstValidatedAt: history?.validatedAt ?? localQr.validatedAt,
        firstValidatedByDeviceId: history?.validatedByDeviceId ?? localQr.validatedByDeviceId,
      };
      persistAfterScan(get, set, eventId, qrId, deviceId, dup, 'duplicate', true);
      return dup;
    }

    const local = scanHistory[qrId];
    if (local && local.scanCount > 0) {
      const dup: ScanResult = {
        result: 'duplicate',
        source: 'offline-cached',
        qrId,
        ticket: localQr.ticketSnapshot,
        scanCount: local.scanCount + 1,
        firstValidatedAt: local.validatedAt,
        firstValidatedByDeviceId: local.validatedByDeviceId,
      };
      persistAfterScan(get, set, eventId, qrId, deviceId, dup, 'duplicate', true);
      return dup;
    }

    const fresh: ScanResult = {
      result: 'valid',
      source: 'offline',
      qrId,
      ticket: localQr.ticketSnapshot,
      scanCount: 1,
      firstValidatedAt: new Date().toISOString(),
      firstValidatedByDeviceId: deviceId,
    };
    persistAfterScan(get, set, eventId, qrId, deviceId, fresh, 'valid', true);
    return fresh;
  },

  async flushPending(eventId, scannerToken) {
    const { pending } = get();
    if (pending.length === 0) return { flushed: 0 };
    set({ isSyncing: true });
    try {
      const res = await fetch(`${API_BASE_URL}/scanner/validate/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Scanner-Token': scannerToken },
        body: JSON.stringify({ validations: pending }),
      });
      if (!res.ok) {
        set({ isOnline: false });
        return { flushed: 0 };
      }
      const json = (await res.json()) as {
        results: Array<{
          qrId: string;
          result: 'valid' | 'duplicate' | 'invalid';
          scanCount?: number;
          validatedAt?: string;
          validatedByDeviceId?: string | null;
        }>;
      };
      const nextHistory = { ...get().scanHistory };
      for (const r of json.results) {
        if (typeof r.scanCount === 'number') {
          nextHistory[r.qrId] = {
            scanCount: r.scanCount,
            validatedAt: r.validatedAt ?? nextHistory[r.qrId]?.validatedAt ?? null,
            validatedByDeviceId:
              r.validatedByDeviceId ?? nextHistory[r.qrId]?.validatedByDeviceId ?? null,
          };
        }
      }
      storage.set(KEYS.pendingValidations(eventId), []);
      storage.set(KEYS.scanHistory(eventId), nextHistory);
      set({ pending: [], scanHistory: nextHistory, isOnline: true });
      return { flushed: pending.length };
    } catch {
      set({ isOnline: false });
      return { flushed: 0 };
    } finally {
      set({ isSyncing: false });
    }
  },

  resetSession(eventId) {
    const fresh = EMPTY_STATS();
    storage.set(KEYS.sessionStats(eventId), fresh);
    set({ session: fresh });
  },
}));

async function callOnlineValidate(
  payload: string,
  deviceId: string,
  scannerToken: string,
): Promise<{
  result: 'valid' | 'duplicate' | 'invalid';
  reason?: InvalidReason;
  qrId?: string;
  ticketSnapshot?: { name: string; kind: string };
  scanCount?: number;
  validatedAt?: string;
  validatedByDeviceId?: string | null;
}> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ONLINE_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE_URL}/scanner/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Scanner-Token': scannerToken },
      body: JSON.stringify({ payload, deviceId, scannedAt: new Date().toISOString() }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`validate: ${res.status}`);
    return (await res.json()) as Awaited<ReturnType<typeof callOnlineValidate>>;
  } finally {
    clearTimeout(timer);
  }
}

function applyOnlineResult(
  get: () => ScannerSyncState,
  set: (partial: Partial<ScannerSyncState>) => void,
  eventId: string,
  qrId: string,
  deviceId: string,
  localQr: SyncBundleQr | undefined,
  online: Awaited<ReturnType<typeof callOnlineValidate>>,
): ScanResult {
  const ticket = online.ticketSnapshot ?? localQr?.ticketSnapshot;
  if (online.result === 'invalid') {
    const reasonStr = online.reason ?? 'unknown';
    const reason: InvalidReason = isInvalidReason(reasonStr) ? reasonStr : 'unknown';
    const next = { ...get().session, invalid: get().session.invalid + 1 };
    storage.set(KEYS.sessionStats(eventId), next);
    set({ session: next });
    return { result: 'invalid', source: 'online', reason };
  }
  const result: ScanResult = {
    result: online.result,
    source: 'online',
    qrId: online.qrId ?? qrId,
    ticket,
    scanCount: online.scanCount,
    firstValidatedAt: online.validatedAt,
    firstValidatedByDeviceId: online.validatedByDeviceId ?? null,
  };
  persistAfterScan(get, set, eventId, online.qrId ?? qrId, deviceId, result, online.result, false);
  return result;
}

function persistAfterScan(
  get: () => ScannerSyncState,
  set: (partial: Partial<ScannerSyncState>) => void,
  eventId: string,
  qrId: string,
  deviceId: string,
  result: ScanResult,
  resolution: 'valid' | 'duplicate',
  enqueue: boolean,
): void {
  const state = get();
  const nextValidated = new Set(state.validatedLocally);
  if (resolution === 'valid') nextValidated.add(qrId);

  const nextHistory: Record<string, ScanHistoryEntry> = { ...state.scanHistory };
  if (typeof result.scanCount === 'number') {
    nextHistory[qrId] = {
      scanCount: result.scanCount,
      validatedAt: result.firstValidatedAt ?? nextHistory[qrId]?.validatedAt ?? null,
      validatedByDeviceId:
        result.firstValidatedByDeviceId ?? nextHistory[qrId]?.validatedByDeviceId ?? null,
    };
  }

  const nextPending = state.pending.slice();
  if (enqueue) {
    nextPending.push({
      qrId,
      validatedAt: result.firstValidatedAt ?? new Date().toISOString(),
      deviceId,
    });
  }

  const nextSession: SessionStats = {
    ...state.session,
    valid: state.session.valid + (resolution === 'valid' ? 1 : 0),
    duplicates: state.session.duplicates + (resolution === 'duplicate' ? 1 : 0),
  };

  storage.set(KEYS.validatedQrs(eventId), [...nextValidated]);
  storage.set(KEYS.scanHistory(eventId), nextHistory);
  storage.set(KEYS.pendingValidations(eventId), nextPending);
  storage.set(KEYS.sessionStats(eventId), nextSession);

  set({
    validatedLocally: nextValidated,
    scanHistory: nextHistory,
    pending: nextPending,
    session: nextSession,
  });
}

function isInvalidReason(s: string): s is InvalidReason {
  return (
    s === 'wrong_event' ||
    s === 'tampered' ||
    s === 'voided' ||
    s === 'malformed' ||
    s === 'unknown'
  );
}

function base64ToBytes(s: string): Uint8Array {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4);
  const bin = window.atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
